import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { UserSecurityDialog } from '@/components/admin/UserSecurityDialog';
import { Users, Ban, CheckCircle2, ShieldCheck, Smartphone, Globe, Fingerprint, Gamepad2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
    device: { icon: Smartphone, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    ip: { icon: Globe, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    fingerprint: { icon: Fingerprint, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
    in_app_id: { icon: Gamepad2, cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const ClusterCard = ({ c, onBlock, onReview, onSecurity }) => {
    const M = TYPE_META[c.type];
    return (
        <Card className={`p-4 ${c.reviewed ? 'opacity-70' : ''}`} data-testid={`multi-cluster-${c.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${M.cls}`}><M.icon className="h-3.5 w-3.5" /> {c.label}</span>
                    <span className="font-mono text-xs text-muted-foreground break-all" data-testid={`multi-cluster-key-${c.id}`}>{c.platform ? `${c.platform} · ` : ''}{c.key}</span>
                    <span className="text-xs text-muted-foreground">{c.users.length} accounts · last {formatDateTime(c.last_at)}</span>
                    {c.reviewed && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Reviewed by {c.reviewed_by}</span>}
                </div>
                <Button size="sm" variant="outline" onClick={() => onReview(c, !c.reviewed)} data-testid={`multi-cluster-review-${c.id}`}>
                    {c.reviewed ? <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark reviewed</>}
                </Button>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Mobile</TableHead><TableHead>State</TableHead><TableHead>Joined</TableHead><TableHead>Last seen on this</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {c.users.map(u => (
                            <TableRow key={u.id} data-testid={`multi-cluster-${c.id}-user-${u.id}`}>
                                <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                                <TableCell className="font-mono text-xs">{u.mobile || '—'}</TableCell>
                                <TableCell className="text-xs">{u.state || '—'}</TableCell>
                                <TableCell className="text-xs whitespace-nowrap">{formatDateTime(u.created_at)}</TableCell>
                                <TableCell className="text-xs whitespace-nowrap">{formatDateTime(u.last_at)} <span className="text-muted-foreground">({u.hits}×)</span></TableCell>
                                <TableCell><span className={`text-xs font-medium ${u.active ? 'text-emerald-600' : 'text-rose-600'}`}>{u.active ? 'Active' : 'Blocked'}</span></TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                    <Button size="sm" variant="ghost" onClick={() => onSecurity(u)} title="Devices & IP history" data-testid={`multi-security-${c.id}-${u.id}`}><ShieldCheck className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => onBlock(u, !u.active)} className={u.active ? 'text-rose-700 border-rose-200' : 'text-emerald-700 border-emerald-200'} data-testid={`multi-block-${c.id}-${u.id}`}>
                                        {u.active ? <><Ban className="h-3.5 w-3.5 mr-1" /> Block</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Unblock</>}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
};

export default function AdminMultiAccounts() {
    const [data, setData] = useState(null);
    const [type, setType] = useState('all');
    const [show, setShow] = useState('open');
    const [secUser, setSecUser] = useState(null);
    const load = () => api.get('/admin/multi-accounts').then(r => setData(r.data)).catch(() => toast.error('Could not load'));
    useEffect(() => { load(); }, []);

    const list = useMemo(() => (data?.clusters || []).filter(c => (type === 'all' || c.type === type) && (show === 'all' || (show === 'open' ? !c.reviewed : c.reviewed))), [data, type, show]);

    const block = async (u, active) => {
        try { await api.patch(`/admin/users/${u.id}`, { active }); toast.success(active ? `${u.email} unblocked` : `${u.email} blocked`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    };
    const review = async (c, reviewed) => {
        try { await api.post('/admin/multi-accounts/review', { cluster_id: c.id, user_ids: c.users.map(u => u.id), reviewed }); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    };

    return (
        <div className="space-y-4" data-testid="admin-multi-accounts-page">
            <Card className="p-4 sm:p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-display font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-[#1F9D8A]" /> Multi-account review</div>
                        <p className="text-xs text-muted-foreground mt-0.5">Accounts that share a device, IP/network, browser fingerprint or recharged the same in-app ID. Blocked users can log in but can't recharge.</p>
                    </div>
                    {data && <div className="text-sm" data-testid="multi-open-count"><strong>{data.open}</strong> open · {data.flagged_user_ids.length} flagged users</div>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    <Select value={type} onValueChange={setType}><SelectTrigger className="w-56" data-testid="multi-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All signals</SelectItem>
                            <SelectItem value="device">Same device</SelectItem>
                            <SelectItem value="ip">Same IP / network</SelectItem>
                            <SelectItem value="fingerprint">Same browser fingerprint</SelectItem>
                            <SelectItem value="in_app_id">Same in-app ID</SelectItem>
                        </SelectContent></Select>
                    <Select value={show} onValueChange={setShow}><SelectTrigger className="w-40" data-testid="multi-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="open">Needs review</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                        </SelectContent></Select>
                </div>
            </Card>
            {!data && <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>}
            {list.map(c => <ClusterCard key={c.id} c={c} onBlock={block} onReview={review} onSecurity={setSecUser} />)}
            {data && list.length === 0 && <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="multi-empty">No suspicious multi-account groups here.</Card>}
            <UserSecurityDialog user={secUser} onClose={() => setSecUser(null)} />
        </div>
    );
}
